import { ApiError, NetworkError, parseProblem } from './errors';
import { session } from './session';
import { audienceOf } from './sessionSync';
import { traceparent, uuid } from './ids';
import type { Page, PurposeOfUse } from './types/common';
import type { TokenResponse } from './types/auth';

/**
 * The single HTTP boundary between the UI and ETNAS core-api.
 *
 * Handles: Bearer token, proactive + reactive refresh (single flight), X-Tenant-Id, X-Purpose-Of-Use,
 * X-Location-Id, X-Step-Up-Token (with automatic step-up prompt + retry on ETNAS_STEP_UP_REQUIRED),
 * If-Match / ETag, Idempotency-Key generation (stable across retries), RFC 9457 problem parsing into
 * typed ApiError, traceparent, and cursor pagination.
 *
 * The transport (`fetch`) is injected so the in-browser mock (VITE_API_MODE=mock) and the real API
 * (VITE_API_MODE=live) share exactly the same code path.
 */

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  /** ETag from a prior GET; sent as If-Match. */
  ifMatch?: string | null;
  /** true → generate an Idempotency-Key; string → use it. Stable across refresh/step-up retries. */
  idempotent?: boolean | string;
  /** Overrides the session's purpose of use for this request (e.g. EMERGENCY during break-glass). */
  purpose?: PurposeOfUse;
  /** Human label shown in the step-up dialog, e.g. "Sign note for Garcia, Maria". */
  stepUpLabel?: string;
  /** false for login/MFA/refresh. */
  auth?: boolean;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  responseType?: 'json' | 'text' | 'blob';
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  etag: string | null;
  requestId: string | null;
  headers: Headers;
}

export interface StepUpPrompt {
  label: string;
  method: string;
  path: string;
}

export interface ClientConfig {
  baseUrl: string;
  fetch: typeof fetch;
  /** Ask the person to re-verify; resolve with a step-up token, or null if they cancel. */
  stepUpHandler?: (p: StepUpPrompt) => Promise<string | null>;
  /** Called once when the session can no longer be refreshed. */
  onSessionExpired?: (reason: 'expired' | 'revoked') => void;
  /** Called after every response (used for connectivity state). */
  onNetworkState?: (online: boolean) => void;
}

const config: ClientConfig = {
  baseUrl: '/api/v1',
  fetch: (...args) => globalThis.fetch(...args),
};

export function configureClient(patch: Partial<ClientConfig>) {
  Object.assign(config, patch);
}

export function getClientConfig(): Readonly<ClientConfig> {
  return config;
}

function buildUrl(path: string, query?: Query): string {
  const qs = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
  }
  const s = qs.toString();
  return config.baseUrl + path + (s ? (path.includes('?') ? '&' : '?') + s : '');
}

let refreshing: Promise<boolean> | null = null;
let sessionRequests = new AbortController();
session.subscribe((_snapshot, reason) => {
  if (reason === 'identity-changed' || reason === 'logout' || reason === 'revoked') {
    sessionRequests.abort(); sessionRequests = new AbortController();
  }
});
/** Set when the last refresh failed only because of rate limiting (429): the session is NOT expired (R-113). */
let refreshRateLimited = false;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const retryAfterMs = (res: Response) => Math.min(5000, Math.max(500, Number(res.headers.get('retry-after') ?? 1) * 1000));

/** POST /auth/refresh using the httpOnly cookie. Single flight: concurrent 401s share one refresh. */
export function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  const generation = sessionRequests.signal;
  const run = async () => {
    refreshRateLimited = false;
    try {
      const call = () =>
        config.fetch(buildUrl('/auth/refresh'), {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json', ...(session.get().tenantId ? { 'X-Tenant-Id': session.get().tenantId! } : {}) },
        });
      let res = await call();
      if (res.status === 429) {
        // Back off once instead of expiring the session on a rate limit.
        await sleep(retryAfterMs(res));
        res = await call();
        if (res.status === 429) {
          refreshRateLimited = true;
          return false;
        }
      }
      if (!res.ok) return false;
      const t = (await res.json()) as TokenResponse;
      // Same person: keep the normalised user and the active tenant; the refresh body's user view
      // (memberships, no active tenant) must not replace them (R-110).
      const cur = session.get().user;
      if (generation.aborted) return false;
      if (cur && (t.user?.id !== cur.id || audienceOf(t.user) !== audienceOf(cur))) {
        session.clear('identity-changed');
        return false;
      }
      session.setTokens(t.accessToken, t.expiresIn, cur ?? (t.user && 'kind' in t.user ? t.user : undefined), { keepContext: true });
      return true;
    } catch {
      return false;
    }
  };
  const p = typeof globalThis.navigator?.locks?.request === 'function' ? navigator.locks.request('etnas.session.refresh', run) : run();
  refreshing = p;
  // Callers already awaiting hold the promise; clear afterwards so the next expiry triggers a fresh refresh.
  p.finally(() => {
    if (refreshing === p) refreshing = null;
  });
  return p;
}

function isAuthPath(path: string) {
  return path.startsWith('/auth/login') || path.startsWith('/auth/mfa') || path.startsWith('/auth/refresh');
}

/**
 * Authenticated calls whose 401 means "that credential was wrong", not "the session expired":
 * never refresh-and-resend (a resent wrong password counts as a second failure) and never end the session (R-041).
 */
function isNoRefreshPath(path: string) {
  return path.startsWith('/auth/step-up') || path.startsWith('/auth/logout');
}

export async function request<T = unknown>(method: string, path: string, opts: RequestOptions = {}): Promise<ApiResponse<T>> {
  const needsAuth = opts.auth !== false && !isAuthPath(path);
  const signal = needsAuth ? (opts.signal ? AbortSignal.any([opts.signal, sessionRequests.signal]) : sessionRequests.signal) : opts.signal;
  const noRefresh = isNoRefreshPath(path);
  let rateRetried = false;
  const idempotencyKey = opts.idempotent === true ? uuid() : typeof opts.idempotent === 'string' ? opts.idempotent : undefined;
  let refreshed = false;
  let steppedUp = false;

  // Proactive refresh shortly before expiry.
  const s0 = session.get();
  if (needsAuth && s0.accessToken && s0.accessExpiresAt && s0.accessExpiresAt - Date.now() < 30_000) {
    const ok = await refreshSession();
    refreshed = true;
    if (!ok && !refreshRateLimited) {
      session.clear('expired');
      config.onSessionExpired?.('expired');
    }
  }

  // eslint-disable-next-line no-constant-condition
  while (true) {
    signal?.throwIfAborted();
    const s = session.get();
    const headers: Record<string, string> = {
      Accept: 'application/json, application/problem+json',
      traceparent: traceparent(),
      ...opts.headers,
    };
    if (needsAuth && s.accessToken) headers.Authorization = `Bearer ${s.accessToken}`;
    if (s.tenantId) headers['X-Tenant-Id'] = s.tenantId;
    headers['X-Purpose-Of-Use'] = opts.purpose ?? s.purpose;
    if (s.locationId) headers['X-Location-Id'] = s.locationId;
    const stepUp = session.validStepUp();
    if (stepUp) headers['X-Step-Up-Token'] = stepUp;
    if (opts.ifMatch) headers['If-Match'] = opts.ifMatch;
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

    let body: BodyInit | undefined;
    if (opts.body !== undefined) {
      if (opts.body instanceof Blob || opts.body instanceof ArrayBuffer || opts.body instanceof FormData) {
        body = opts.body as BodyInit;
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(opts.body);
      }
    }

    let res: Response;
    try {
      res = await config.fetch(buildUrl(path, opts.query), { method, headers, body, credentials: 'include', signal });
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') throw e;
      config.onNetworkState?.(false);
      throw new NetworkError(undefined, e);
    }
    config.onNetworkState?.(true);

    if (res.ok) {
      const requestId = res.headers.get('x-request-id');
      const etag = res.headers.get('etag');
      let data: unknown = undefined;
      if (res.status !== 204) {
        const rt = opts.responseType ?? 'json';
        if (rt === 'blob') data = await res.blob();
        else if (rt === 'text') data = await res.text();
        else {
          const text = await res.text();
          data = text ? JSON.parse(text) : undefined;
        }
      }
      signal?.throwIfAborted();
      return { data: data as T, status: res.status, etag, requestId, headers: res.headers };
    }

    // Rate limited: back off once for safe reads (R-113); commands surface the 429 to the person.
    if (res.status === 429 && !rateRetried && (method === 'GET' || idempotencyKey)) {
      rateRetried = true;
      await sleep(retryAfterMs(res));
      continue;
    }

    const problem = await parseProblem(res);
    const err = new ApiError(problem, res.headers.get('x-request-id') ?? undefined);

    if (noRefresh && res.status === 401 && err.code !== 'ETNAS_STEP_UP_REQUIRED') throw err;

    if (needsAuth && res.status === 401 && err.code === 'ETNAS_STEP_UP_REQUIRED') {
      session.clearStepUp();
      if (!steppedUp && config.stepUpHandler) {
        steppedUp = true;
        const token = await config.stepUpHandler({ label: opts.stepUpLabel ?? describeCommand(method, path), method, path });
        if (token) continue; // handler stored the token in the session; retry with the same Idempotency-Key
      }
      throw err;
    }

    if (needsAuth && res.status === 401 && !refreshed) {
      refreshed = true;
      const ok = await refreshSession();
      if (ok) continue;
      if (refreshRateLimited) throw err;
      session.clear('expired');
      config.onSessionExpired?.('expired');
      throw err;
    }
    if (needsAuth && res.status === 401) {
      session.clear('expired');
      config.onSessionExpired?.('expired');
    }
    throw err;
  }
}

/** "POST /notes/{id}:sign" → "Sign note" for step-up prompts when no label was given. */
export function describeCommand(method: string, path: string): string {
  const m = path.match(/\/([a-z-]+)\/[^/:]+:([a-z-]+)$/i) || path.match(/\/([a-z-]+):([a-z-]+)$/i);
  if (m) {
    const noun = m[1].replace(/s$/, '').replace(/-/g, ' ');
    const verb = m[2].replace(/-/g, ' ');
    return `${verb.charAt(0).toUpperCase()}${verb.slice(1)} ${noun}`;
  }
  if (path.startsWith('/break-glass')) return 'Start emergency access';
  return `${method} ${path}`;
}

/* ---------- Convenience wrappers ---------- */

export async function get<T>(path: string, query?: Query, opts: Omit<RequestOptions, 'query' | 'body'> = {}): Promise<T> {
  return (await request<T>('GET', path, { ...opts, query })).data;
}

/** GET a mutable aggregate together with its ETag. */
export async function getVersioned<T>(path: string, query?: Query, opts: Omit<RequestOptions, 'query' | 'body'> = {}) {
  const r = await request<T>('GET', path, { ...opts, query });
  return { data: r.data, etag: r.etag };
}

export async function post<T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}): Promise<T> {
  return (await request<T>('POST', path, { ...opts, body })).data;
}

export async function postVersioned<T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}) {
  const r = await request<T>('POST', path, { ...opts, body });
  return { data: r.data, etag: r.etag };
}

export async function put<T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}): Promise<T> {
  return (await request<T>('PUT', path, { ...opts, body })).data;
}

export async function putVersioned<T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}) {
  const r = await request<T>('PUT', path, { ...opts, body });
  return { data: r.data, etag: r.etag };
}

export async function patch<T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}) {
  const r = await request<T>('PATCH', path, { ...opts, body });
  return { data: r.data, etag: r.etag };
}

/** Fetch every page of a cursor-paginated list (bounded). Use for small reference lists only. */
export async function getAll<T>(path: string, query: Query = {}, maxPages = 10): Promise<Page<T>> {
  const items: T[] = [];
  let cursor: string | null | undefined = undefined;
  let restricted = 0;
  for (let i = 0; i < maxPages; i++) {
    const page: Page<T> = await get<Page<T>>(path, { ...query, cursor: cursor ?? undefined, limit: query.limit ?? 100 });
    items.push(...page.items);
    restricted += page.restricted ?? 0;
    cursor = page.nextCursor;
    if (!cursor) break;
  }
  return { items, nextCursor: cursor ?? null, restricted: restricted || undefined };
}

/** Encode a path segment (ids are UUIDs but never trust that). */
export const seg = (v: string) => encodeURIComponent(v);

/** Weak ETag for a backend aggregate's rowVersion (the backend accepts `W/"<rowVersion>"` as If-Match). */
export const etagFromRow = (rowVersion: string | number | null | undefined): string | null =>
  rowVersion === null || rowVersion === undefined || rowVersion === '' ? null : `W/"${rowVersion}"`;
